/**
 * Billing Banner Component
 * App-level banners for trial countdown, payment failure, and deactivation
 */

import { Link, useLocation } from 'react-router-dom';
import { useBillingInvoices, useBillingSubscription } from '../hooks/useBilling';
import { formatPeriodDate } from '../api/billing';

/** The plan choices live here, so the trial banner needs no Subscribe link on this page. */
const BILLING_PATH = '/settings/billing';
/** Routes that carry their own checkout message; the trial banner would contradict it (BR-86). */
const CHECKOUT_PATHS = [`${BILLING_PATH}/checkout`, `${BILLING_PATH}/success`];

/** React Router matches a route with or without a trailing slash; compare both the same way. */
function withoutTrailingSlash(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

export function BillingBanner() {
  const path = withoutTrailingSlash(useLocation().pathname);
  const { data: subscription } = useBillingSubscription();
  const isPastDue = subscription?.status === 'past_due';
  // The failed payment's date (its invoice's due date); the banner reads without it until loaded.
  const { data: invoicesData } = useBillingInvoices(isPastDue);
  const failedInvoice = invoicesData?.invoices
    .filter((inv) => inv.status === 'failed')
    .sort((a, b) => b.dueDate.localeCompare(a.dueDate))[0];

  if (!subscription) {
    return null;
  }

  // Deactivation notice (highest priority): only when billing says the account is deactivated, never inferred
  // from a missing subscription (an out-of-band activation or a failed read leaves none). A resubscribing
  // account is still deactivated until the card confirm lands, but checkout owns the message there.
  if (subscription.accountDeactivated && !CHECKOUT_PATHS.includes(path)) {
    return (
      <div className="px-5 py-3 bg-destructive text-destructive-foreground text-center text-sm font-medium">
        Your account is deactivated. Subscribe to regain access.{' '}
        <Link
          to="/settings/billing"
          className="text-destructive-foreground underline font-semibold"
        >
          Resubscribe →
        </Link>
      </div>
    );
  }

  // Past due (BR-37): the plan keeps working until the grace deadline.
  if (subscription.status === 'past_due') {
    return (
      <div
        role="alert"
        data-testid="billing-past-due-banner"
        className="px-5 py-3 bg-warning/10 border-b border-warning/25 text-warning text-center text-sm font-medium"
      >
        <strong data-testid="billing-past-due-heading">
          {failedInvoice
            ? `Your ${formatPeriodDate(failedInvoice.dueDate)} payment didn\u2019t go through.`
            : 'Your payment didn\u2019t go through.'}
        </strong>{' '}
        {subscription.graceDeadline
          ? `Update your payment method by ${formatPeriodDate(subscription.graceDeadline, 'UTC', true)} to keep your account active. `
          : 'Update your payment method to keep your account active. '}
        Everything keeps working until then.{' '}
        <Link
          to="/settings/billing"
          data-testid="billing-past-due-update"
          className="inline-flex items-center min-h-[44px] text-warning underline font-semibold"
        >
          Update payment method →
        </Link>
      </div>
    );
  }

  // Trial (US-2): shown for the whole trial; the final 7 days escalate to the warning tier (BR-4).
  if (subscription.status === 'trial' && subscription.trialDaysRemaining !== null) {
    // Checkout and checkout-success wait for the card confirm to activate the plan: one message there.
    if (CHECKOUT_PATHS.includes(path)) return null;
    return (
      <TrialBanner
        daysRemaining={subscription.trialDaysRemaining}
        showSubscribeLink={path !== BILLING_PATH}
      />
    );
  }

  return null;
}

/** Days left at or below which the trial banner escalates to the warning tier (BR-4). */
const TRIAL_WARNING_DAYS = 7;

function trialText(days: number): string {
  if (days <= 0) return 'Your free trial ends today.';
  return `You're on a free trial: ${days} day${days === 1 ? '' : 's'} left.`;
}

function TrialBanner({ daysRemaining, showSubscribeLink }: { daysRemaining: number; showSubscribeLink: boolean }) {
  const warning = daysRemaining <= TRIAL_WARNING_DAYS;
  return (
    <div
      role="status"
      data-testid="billing-trial-banner"
      data-variant={warning ? 'warning' : 'info'}
      className={`px-5 py-1 border-b text-center text-sm font-medium ${
        warning ? 'bg-warning/10 border-warning/25 text-warning' : 'bg-info/10 border-info/25 text-info'
      }`}
    >
      {trialText(daysRemaining)}
      {showSubscribeLink && (
        <>
          {' '}
          <Link
            to={BILLING_PATH}
            data-testid="billing-trial-subscribe"
            className={`inline-flex items-center min-h-[44px] underline font-semibold ${
              warning ? 'text-warning' : 'text-info'
            }`}
          >
            Subscribe →
          </Link>
        </>
      )}
    </div>
  );
}

export default BillingBanner;
